// The arcade ladder in the chrome (arcade.md §18.5, §18.6, R1616).
//
// The owner picked cards 01 to 05 of mockup round 078 with the tier names
// of set A, the water. This leaf holds what the five surfaces share: the
// ladder tier of a ladder number, the ladder mark, the bar through one
// tier, the lobby card, the end card block and the line of the last rated
// matches. The hub panel RANKS, the LADDER section of the arcade lobby
// (`arcade/ladderpage.ts`, R1948) and the end card call it; it reads no state and writes no element, so every builder is a
// pure function of its arguments and the tests read the markup back.
//
// The client computes no rating. The meta server writes the ladder number
// (the rating less two deviations, arcade.md §18.1), and the tier is read
// from it here by the bands of `[meta.ladder] tiers`, so the tier and the
// number always agree. Only the LADDER section prints the number (§18.6).

import { LADDER_START, LADDER_TIER_FROM } from '../gen/balance';
import { ladder as S } from '../gen/strings';
import {
  ArcadeLadderStatus, type ArcadePlayerStats, type ArcadeSummary,
} from '../gen/proto/aquaphobia';
import { groupThousands } from '../locale/group';
import { SEC_PER_DAY } from '../locale/clock';
import type { LadderCard, LadderMatch } from '../net/api';
import { esc } from './esc';
import { clamp01 } from '../render/math';

/** How many ladder tiers the bands make: one more than the figures. */
export const TIER_COUNT = LADDER_TIER_FROM.length + 1;

/** The ladder tier of a ladder number, 1 to `TIER_COUNT`. A number below
 *  every band is the first tier, so a number below zero is one too. */
export function tierOf(n: number): number {
  let t = 1;
  for (const from of LADDER_TIER_FROM) {
    if (n < from) break;
    t++;
  }
  return t;
}

/** A tier kept inside 1 to `TIER_COUNT`. */
function clampTier(t: number): number {
  return Math.min(TIER_COUNT, Math.max(1, Math.trunc(t)));
}

/** The name of a ladder tier, in the picked language. */
export function tierName(t: number): string {
  return S.tier_names[clampTier(t) - 1] ?? '';
}

/** The two ends of the bar of one tier. `hi` is the number at which the
 *  next tier starts, and null for the last tier, whose bar is full. */
export interface TierBounds { lo: number; hi: number | null }

/**
 * The bounds of the bar of one tier.
 *
 * The first tier has no lower band, so its bar starts at the start ladder
 * number: a new account stands there with an empty bar, and the climb
 * fills it (R1619).
 */
export function tierBounds(t: number): TierBounds {
  const tier = clampTier(t);
  const hi = tier === TIER_COUNT ? null : (LADDER_TIER_FROM[tier - 1] as number);
  const lo = tier === 1 ? LADDER_START : (LADDER_TIER_FROM[tier - 2] as number);
  return { lo, hi };
}

/** How far a ladder number stands through the bar of tier `t`, from 0 to
 *  1. A number outside the tier is held at an end; the last tier is
 *  always full, because it has no top. */
export function barShare(n: number, t = tierOf(n)): number {
  const { lo, hi } = tierBounds(t);
  if (hi === null) return 1;
  if (hi <= lo) return n >= hi ? 1 : 0;
  return clamp01((n - lo) / (hi - lo));
}

/** A figure as the ladder prints it: the thin-space group. */
export function ladderFigure(n: number): string {
  return groupThousands(n);
}

// ---- the ladder mark ----------------------------------------------------

/**
 * The chevrons of one step, as polyline points on the 24-unit grid of
 * card 01. They are written out, so no float sum reaches the markup.
 */
const CHEVRONS: readonly (readonly string[])[] = [
  ['9,11.9 12,14.7 15,11.9'],
  ['9,10.1 12,12.9 15,10.1', '9,13.9 12,16.7 15,13.9'],
  ['9,8.7 12,11.5 15,8.7', '9,12 12,14.8 15,12', '9,15.3 12,18.1 15,15.3'],
];

/** The token of the ink of a tier: the text ink, the game accent, then
 *  the gold, two tiers each. The middle two read the frozen
 *  `--game-accent` and never `--accent`: `--accent` wears the banner, and
 *  the amber of the Rust Fleet stood too near the gold (R1619). */
export type LadderInk = '--text' | '--game-accent' | '--gold';

function inkTokenOf(t: number): LadderInk {
  if (t <= 2) return '--text';
  if (t <= 4) return '--game-accent';
  return '--gold';
}

/** The ink of a tier as a CSS value. RANKS writes a tier's name in the
 *  same ink. */
export function inkOf(t: number): string {
  return `var(${inkTokenOf(t)})`;
}

/** One circle of the mark, stroked: the eye of the lead or the ring. */
export interface LadderCircle { cx: number; cy: number; r: number; width: number; opacity: number }

/**
 * The geometry of the ladder mark of one tier, on the 24-unit grid of
 * card 01. `ladderMark` writes it as SVG, and the Discord emoji of a tier
 * draws it on a canvas (brand.md §14.3), so the two never part.
 *
 * Every part draws in the tier's ink, except two: the fill of an open lead
 * takes the panel ink at `panelAlpha`, and the chevrons of a filled lead
 * take the panel ink.
 */
export interface LadderMarkShape {
  readonly tier: number;
  readonly ink: LadderInk;
  /** The lead is filled in the tier's ink in the second half of the tiers. */
  readonly filled: boolean;
  /** The ring of the last tier, or null. */
  readonly ring: LadderCircle | null;
  /** The eye of the lead, where its line is made fast. */
  readonly eye: LadderCircle;
  /** The line from the eye down to the lead. */
  readonly line: { x1: number; y1: number; x2: number; y2: number; width: number };
  /** The drop of the lead, as SVG path data, and its stroke. */
  readonly lead: { d: string; width: number };
  /** The alpha of the panel ink in an open lead. */
  readonly panelAlpha: number;
  /** The chevrons, as SVG polyline points, and their stroke. */
  readonly chevrons: { points: readonly string[]; width: number };
}

/** The geometry of the ladder mark of tier `t` (card 01). */
export function ladderMarkShape(t: number): LadderMarkShape {
  const tier = clampTier(t);
  return {
    tier,
    ink: inkTokenOf(tier),
    filled: tier > 3,
    ring: tier === TIER_COUNT ? { cx: 12, cy: 13.2, r: 11.2, width: 1, opacity: 0.75 } : null,
    eye: { cx: 12, cy: 2.9, r: 1.7, width: 1.1, opacity: 1 },
    line: { x1: 12, y1: 4.6, x2: 12, y2: 6.4, width: 1.1 },
    lead: { d: 'M8.6 6.4H15.4L17.6 12L12 22.6L6.4 12Z', width: 1.3 },
    panelAlpha: 0.85,
    chevrons: { points: CHEVRONS[(tier - 1) % 3] ?? [], width: 1.45 },
  };
}

/** An alpha as the markup writes it, with no leading zero: `.75`. */
function alphaAttr(a: number): string {
  return String(a).replace(/^0\./, '.');
}

/**
 * The ladder mark of one tier, at `px` CSS pixels (card 01).
 *
 * A sounding lead: the drop points down, with one to three chevrons that
 * point down. The lead is open in the first half of the tiers and filled
 * in the second; the count of chevrons is the step inside the half; the
 * last tier wears a ring. So the six read apart in grey. The SVG draws in
 * `currentColor` under the tier's ink, and the open lead and the chevrons
 * of a filled lead take the panel ink, so the mark reads on every ground.
 */
export function ladderMark(t: number, px: number): string {
  const m = ladderMarkShape(t);
  let o = `<svg class="lmark" width="${px}" height="${px}" viewBox="0 0 24 24" role="img"`
    + ` aria-label="${esc(tierName(m.tier))}" style="color:var(${m.ink})">`;
  if (m.ring) {
    o += `<circle cx="${m.ring.cx}" cy="${m.ring.cy}" r="${m.ring.r}" fill="none" stroke="currentColor"`
      + ` stroke-width="${m.ring.width}" opacity="${alphaAttr(m.ring.opacity)}"/>`;
  }
  o += `<circle cx="${m.eye.cx}" cy="${m.eye.cy}" r="${m.eye.r}" fill="none" stroke="currentColor" stroke-width="${m.eye.width}"/>`
    + `<line x1="${m.line.x1}" y1="${m.line.y1}" x2="${m.line.x2}" y2="${m.line.y2}" stroke="currentColor" stroke-width="${m.line.width}"/>`
    + `<path d="${m.lead.d}"`
    + (m.filled ? ' fill="currentColor"' : ` style="fill:rgb(var(--panel-rgb) / ${alphaAttr(m.panelAlpha)})"`)
    + ` stroke="currentColor" stroke-width="${m.lead.width}" stroke-linejoin="round"/>`;
  const chevron = m.filled ? 'style="stroke:rgb(var(--panel-rgb))"' : 'stroke="currentColor"';
  for (const points of m.chevrons.points) {
    o += `<polyline points="${points}" fill="none" ${chevron} stroke-width="${m.chevrons.width}"`
      + ' stroke-linecap="round" stroke-linejoin="round"/>';
  }
  return `${o}</svg>`;
}

/** The mark and the name of a tier on one line, the form RANKS and the
 *  RECORD card print beside a word. */
export function tierLabel(t: number, px = 16): string {
  return `<span class="ltier">${ladderMark(t, px)}${esc(tierName(t))}</span>`;
}

// ---- the bar ------------------------------------------------------------

/** One fill of the bar: from and to are shares of the bar, 0 to 1. */
export interface BarPart { from: number; to: number; cls?: 'was' | 'gain' | 'lost' }

function pct(v: number): string {
  return String(Math.round(clamp01(v) * 100));
}

/** The bar through one tier. A part of no width draws nothing. */
export function ladderBar(parts: readonly BarPart[]): string {
  return '<div class="lbar">' + parts
    .filter((p) => Math.round(p.to * 100) > Math.round(p.from * 100))
    .map((p) => `<i${p.cls === undefined ? '' : ` class="${p.cls}"`}`
      + ` style="left:${pct(p.from)}%;width:${pct(p.to - p.from)}%"></i>`)
    .join('') + '</div>';
}

/** The two words under the bar of a tier: its name, and the next tier,
 *  or the word that no tier follows. */
function barEnds(t: number): string {
  const next = t < TIER_COUNT ? S.next({ name: tierName(t + 1) }) : S.last_tier;
  return `<div class="lnx"><span>${esc(tierName(t))}</span><span>${esc(next)}</span></div>`;
}

// ---- the lobby card (card 02) -------------------------------------------

/** The wins and the losses of the last seven days of a history: THIS WEEK
 *  (R1619). */
export function weekTally(history: readonly LadderMatch[], nowSec: number): { won: number; lost: number } {
  const since = nowSec - 7 * SEC_PER_DAY;
  let won = 0;
  let lost = 0;
  for (const m of history) {
    if (m.at < since) continue;
    if (m.outcome === 'won') won++;
    else if (m.outcome === 'lost') lost++;
  }
  return { won, lost };
}

/**
 * The LADDER card of the lobby's side column (card 02).
 *
 * It prints no number, as the owner ruled: the mark, the tier name, the bar
 * through the tier, the week, and THE LADDER, which opens the LADDER
 * section of the lobby, where the number stands (R1948). A guest
 * has no ladder (R1603), so its card says that an account climbs it and
 * offers REGISTER. A card the meta server has not answered yet draws
 * nothing, and so does a lane with no meta server.
 */
export function lobbyLadderCard(card: LadderCard | null, guest: boolean, nowSec: number): string {
  const head = '<div class="row"><span class="ttl">'
    + `${S.lobby_title}</span><span class="grow"></span><span class="kick">${S.lobby_kick}</span></div>`;
  if (guest) {
    return `<div class="panel" id="arc-ladder">${head}`
      + `<div class="reason" style="margin-top:8px">${S.guest_line}</div>`
      + '<div class="meta" style="margin-top:6px">'
      // A link of the lobby is a span that wears the role and the tab stop
      // of a button, so Enter and Space press it (R1701).
      + `<span class="lnk" data-a="register" role="button" tabindex="0">`
      + `${S.guest_register}</span></div></div>`;
  }
  if (card === null) return '';
  const t = tierOf(card.ladder);
  const week = weekTally(card.history, nowSec);
  return `<div class="panel" id="arc-ladder">${head}`
    + `<div class="lad">${ladderMark(t, 40)}<div class="lt">`
    + `<span class="ltn">${esc(tierName(t))}<small>${S.tier_of({ n: t, total: TIER_COUNT })}</small></span>`
    + ladderBar([{ from: 0, to: barShare(card.ladder, t) }])
    + barEnds(t)
    + '</div></div>'
    + '<div class="meta" style="margin-top:8px">'
    + `<span>${S.lobby_week({ won: week.won, lost: week.lost })}</span>`
    + `<span class="lnk" data-a="ladder" role="button" tabindex="0">${S.lobby_open}</span></div>`
    + '</div>';
}

// ---- the end card block (card 03) ---------------------------------------

/** The hatched loss and the green gain of the bar of tier `t`, from the
 *  ladder number before to the one after. A number outside the tier is
 *  held at the end of the bar it passed. */
function changeBar(t: number, before: number, after: number): string {
  const b = barShare(before, t);
  const a = barShare(after, t);
  if (a >= b) {
    return ladderBar([{ from: 0, to: b, cls: 'was' }, { from: b, to: a, cls: 'gain' }]);
  }
  return ladderBar([{ from: 0, to: a }, { from: a, to: b, cls: 'lost' }]);
}

/** The words of the change: UP n, DOWN n, or no change. */
function changeWords(delta: number): { words: string; cls: string } {
  if (delta > 0) return { words: S.end_up({ n: ladderFigure(delta) }), cls: 'up' };
  if (delta < 0) return { words: S.end_down({ n: ladderFigure(-delta) }), cls: 'down' };
  return { words: S.end_level, cls: '' };
}

/**
 * The LADDER block of the end card (card 03), or the one line that stands
 * in its place.
 *
 * The block names the change in words and never prints the ladder number.
 * A rise into a higher tier draws the old tier's bar filled with the gain
 * and stamps the new mark; a fall out of a tier draws the new tier's bar
 * with the loss hatched and says so in one line. A private game prints
 * its own line, and a public match that did not rate the reader's seat
 * prints one. A card with no ladder answer, a drill (its own card says
 * it) and a spectator's card print nothing (R1619).
 */
export function endLadderBlock(s: ArcadeSummary, ownSlot: number, viewer = false): string {
  if (viewer) return '';
  switch (s.ladder) {
    case ArcadeLadderStatus.ARCADE_LADDER_PRIVATE:
      return `<div class="lfine">${S.end_private}</div>`;
    case ArcadeLadderStatus.ARCADE_LADDER_UNRATED:
      return `<div class="lfine">${S.end_unrated}</div>`;
    case ArcadeLadderStatus.ARCADE_LADDER_RATED:
      break;
    default:
      return '';
  }
  const row: ArcadePlayerStats | undefined = s.rows.find((r) => r.slot === ownSlot);
  if (row === undefined || !row.rated) return `<div class="lfine">${S.end_unrated}</div>`;
  const before = row.ladderBefore;
  const after = row.ladderAfter;
  const t0 = tierOf(before);
  const t1 = tierOf(after);
  const change = changeWords(after - before);
  const head = `<div class="lh"><span>${S.end_head}</span>`
    + `<b${change.cls === '' ? '' : ` class="${change.cls}"`}>${esc(change.words)}</b></div>`;
  let body: string;
  if (t1 > t0) {
    body = `<div class="lad">${ladderMark(t0, 30)}<div class="lt">`
      + `<span class="lsub">${esc(tierName(t0))}</span>${changeBar(t0, before, after)}</div></div>`
      + `<div class="lmove rise"><span class="lstamp">${ladderMark(t1, 34)}</span>`
      + `<span>${S.end_move({ from: esc(tierName(t0)), to: esc(tierName(t1)) })}</span></div>`;
  } else {
    body = `<div class="lad">${ladderMark(t1, 30)}<div class="lt">`
      + `<span class="lsub">${esc(S.end_tier({ name: tierName(t1), n: t1, total: TIER_COUNT }))}</span>`
      + `${changeBar(t1, before, after)}</div></div>`
      + (t1 < t0
        ? `<div class="lmove fall">${S.end_move({ from: esc(tierName(t0)), to: esc(tierName(t1)) })}</div>`
        : '');
  }
  return `<div class="lblock">${head}${body}</div><div class="lfine">${S.end_fine}</div>`;
}

// ---- the line of the LADDER section (card 04) ---------------------------

/** The ladder numbers of a history, the oldest first: the number before
 *  the oldest match, then the number after each. */
export function historyLine(history: readonly LadderMatch[]): number[] {
  if (history.length === 0) return [];
  const oldest = history[history.length - 1] as LadderMatch;
  const out = [oldest.before];
  for (let i = history.length - 1; i >= 0; i--) out.push((history[i] as LadderMatch).after);
  return out;
}

/** The drawing box of the line, in CSS px of height and in per cent of
 *  the card's width. The SVG stretches to the card and keeps its strokes
 *  at one width; the tier names and the end dot are HTML over it, so the
 *  text keeps its size on a wide card. */
const SPARK_W = 100;
const SPARK_H = 64;
const SPARK_PAD = 4;

/**
 * The line of the last rated matches across the dashed bounds of the
 * tiers (card 04). Each bound inside the drawn span carries the name of
 * the tier it opens. A history of fewer than two points draws nothing.
 */
export function ladderSpark(points: readonly number[]): string {
  if (points.length < 2) return '';
  const min = Math.min(...points);
  const max = Math.max(...points);
  const pad = Math.max(60, (max - min) * 0.2);
  const lo = min - pad;
  const hi = max + pad;
  const x = (i: number): string => (1 + (i / (points.length - 1)) * (SPARK_W - 2)).toFixed(2);
  const y = (v: number): string =>
    (SPARK_H - SPARK_PAD / 2 - ((v - lo) / (hi - lo)) * (SPARK_H - SPARK_PAD)).toFixed(1);
  let lines = '';
  let names = '';
  for (const b of LADDER_TIER_FROM) {
    if (b <= lo || b >= hi) continue;
    lines += `<line x1="0" x2="${SPARK_W}" y1="${y(b)}" y2="${y(b)}" stroke-dasharray="3 3"`
      + ' vector-effect="non-scaling-stroke" style="stroke:rgb(var(--accent-rgb) / .14)"/>';
    names += `<span class="lb" style="top:${y(b)}px">${esc(tierName(tierOf(b)))}</span>`;
  }
  const last = points.length - 1;
  return '<div class="lspark" aria-hidden="true">'
    + `<svg viewBox="0 0 ${SPARK_W} ${SPARK_H}" preserveAspectRatio="none">${lines}`
    + `<polyline points="${points.map((v, i) => `${x(i)},${y(v)}`).join(' ')}"`
    + ' fill="none" stroke-width="1.6" stroke-linejoin="round" vector-effect="non-scaling-stroke"'
    + ' style="stroke:var(--accent)"/></svg>'
    + names
    + `<i class="ldot" style="left:${x(last)}%;top:${y(points[last] as number)}px"></i>`
    + '</div>';
}
